import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {ChartingLibraryWidgetOptions, IChartingLibraryWidget, ResolutionString, widget,} from '../../charting_library';
import {observer} from 'mobx-react-lite';
import './TVChartContainer.scss';
import {marketsStore} from "../../stores/MarketsStore";
import {datafeed} from "./datafeed";
import {chartStore} from "../../stores/ChartStore";
import {formatPrice} from "../../helpers/formatters";

export interface ChartContainerProps {
    symbol: ChartingLibraryWidgetOptions['symbol'];
    interval: ChartingLibraryWidgetOptions['interval'];
    libraryPath: ChartingLibraryWidgetOptions['library_path'];
    chartsStorageUrl: ChartingLibraryWidgetOptions['charts_storage_url'];
    chartsStorageApiVersion: ChartingLibraryWidgetOptions['charts_storage_api_version'];
    clientId: ChartingLibraryWidgetOptions['client_id'];
    userId: ChartingLibraryWidgetOptions['user_id'];
    fullscreen: ChartingLibraryWidgetOptions['fullscreen'];
    autosize: ChartingLibraryWidgetOptions['autosize'];
    studiesOverrides: ChartingLibraryWidgetOptions['studies_overrides'];
    container: ChartingLibraryWidgetOptions['container'];
}

interface TVChartContainerProps {
    baseAsset: string;
    quoteAsset: string;
    colors?: {
        backgroundColor?: string;
        lineColor?: string;
        textColor?: string;
        areaTopColor?: string;
        areaBottomColor?: string;
    };
}
export const TVChartContainer: React.FC<TVChartContainerProps> =  observer(({ baseAsset, quoteAsset, colors }) => {
    const {
        backgroundColor = '#04121C',
        // lineColor = '#2962FF',
        // textColor = '#4b5982',
        // areaTopColor = '#2962FF',
        // areaBottomColor = 'rgba(41, 98, 255, 0.28)'
    } = colors || {};
    const chartContainerRef = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
    const widgetRef = useRef<IChartingLibraryWidget>();
    const [isWidgetReady, setWidgetReady] = useState<boolean>(false)

    const defaultProps: Omit<ChartContainerProps, 'container'> = {
        symbol: marketsStore.currentMarket?.name ?? '',
        interval: '1D' as ResolutionString,
        libraryPath: '/charting_library/',
        chartsStorageUrl: 'https://saveload.tradingview.com',
        chartsStorageApiVersion: '1.1',
        clientId: 'tradingview.com',
        userId: 'public_user_id',
        fullscreen: false,
        autosize: true,
        studiesOverrides: {}
    };

    useEffect(() => {
        const widgetOptions: ChartingLibraryWidgetOptions = {
            symbol: defaultProps.symbol as string,
            datafeed,
            interval: defaultProps.interval as ChartingLibraryWidgetOptions['interval'],
            time_frames: [
                //list at the bottom of the chart
                { text: "1d", resolution: "5" as ResolutionString, title: '1D' },
                { text: "5d", resolution: "60" as ResolutionString, title: '5D' },
                { text: "1m", resolution: "240" as ResolutionString, title: '1M' },
                { text: "3m", resolution: "1D" as ResolutionString, title: '3M' },
                { text: "6m", resolution: "1D" as ResolutionString, title: '6M' },
                { text: "1y", resolution: "1D" as ResolutionString, title: '1Y' },
            ],
            container: chartContainerRef.current,
            library_path: defaultProps.libraryPath as string,
            locale: 'en',
            disabled_features: [
                'use_localstorage_for_settings',
                'header_symbol_search',
                'symbol_search_hot_key',
                'header_compare',
                'legend_context_menu',
                'popup_hints',
                'header_saveload',
                'symbol_info',
                'legend_inplace_edit',
                'header_undo_redo'
            ],
            enabled_features: [
                'hide_resolution_in_legend'
            ],
            custom_formatters: {
                priceFormatterFactory: (symbolInfo, minTick) => {
                    if (symbolInfo === null) {
                        return null;
                    }

                    if (symbolInfo.format === 'price') {
                        return {
                            format: (price, signPositive) => {
                                return formatPrice(price)
                            },
                        };
                    }
                    return null; // The default formatter will be used.
                },
            },
            charts_storage_url: defaultProps.chartsStorageUrl,
            charts_storage_api_version: defaultProps.chartsStorageApiVersion,
            client_id: defaultProps.clientId,
            user_id: defaultProps.userId,
            fullscreen: defaultProps.fullscreen,
            autosize: defaultProps.autosize,
            studies_overrides: defaultProps.studiesOverrides,
            theme: 'dark',
            toolbar_bg: backgroundColor,
            overrides: {
                "paneProperties.background": backgroundColor,
                "paneProperties.backgroundType": "solid",
                "paneProperties.vertGridProperties.color": backgroundColor,
                "paneProperties.horzGridProperties.color": backgroundColor,
            },
            loading_screen: { backgroundColor: backgroundColor },
            // debug: true
        };

        widgetRef.current = new widget(widgetOptions);

        widgetRef.current.onChartReady(() => {
            if (widgetRef.current) {
                widgetRef.current.headerReady().then(() => {
                    widgetRef.current?.setCSSCustomProperty('--tv-color-pane-background', backgroundColor)
                    setWidgetReady(true)
                });
            }

        });

        return () => {
            if (widgetRef.current) {
                widgetRef.current.remove();
            }
        };
    }, []);

    useEffect(() => {
        if (isWidgetReady && widgetRef.current && marketsStore.currentMarket) {
            const currentResolution = widgetRef.current.activeChart().resolution()
            chartStore.fetchDailyBars(marketsStore.currentMarket.baseAsset, marketsStore.currentMarket.quoteAsset)
            widgetRef.current.setSymbol(marketsStore.currentMarket.name, currentResolution, () => {
            });
        }
    }, [marketsStore.currentMarket, isWidgetReady]);

    return (
        <div
            ref={ chartContainerRef }
            className={ 'TVChartContainer' }
        />
    );
});
